// import { UserGame } from './types'; // Comentado para resolver conflito de tipo
import { supabase } from '../supabase';
// Usar a importação de tipos apenas de um local
import type { UserProfile, GameStats } from '../../types';
import type { Game as AppGame, UserGame as AppUserGame } from '../../types';
// Adicionar imports para gamificação
import { processAction } from '../gamification/gamificationService';
import { XP_ACTIONS } from '../gamification/xpConfig';
import { PostgrestError, PostgrestResponse, PostgrestMaybeSingleResponse } from '@supabase/supabase-js'; // Importar PostgrestError

// Ajustar Game localmente para lidar com stats: Json do banco
export type DbGame = Omit<AppGame, 'stats'> & { stats: GameStats | null }; // Usa GameStats | null
export type Game = AppGame;

export type DbUserGame = Omit<AppUserGame, 'play_count'> & { play_count: number | null }; 
export type UserGame = AppUserGame;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
const userCollectionCache = new Map<string, { timestamp: number; data: any }>();

// Funções de Cache Refatoradas

const isOffline = () => typeof navigator !== 'undefined' && !navigator.onLine;

const getCachedData = (key: string) => {
  if (isOffline()) {
    const cached = userCollectionCache.get(key);
    if (cached) {
      console.log(`[Cache] Retornando dados offline para ${key}`);
      return cached.data;
    }
  }
  const cached = userCollectionCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`[Cache] Retornando dados em cache para ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  console.log(`[Cache] Armazenando dados para ${key}`);
  userCollectionCache.set(key, { timestamp: Date.now(), data });
};

const invalidateUserCache = (userId: string) => {
  console.log(`[Cache] Invalidando cache para usuário ${userId}`);
  // Invalidar chaves específicas relacionadas ao usuário
  const keysToRemove = [
    `collection-${userId}`,
    `wishlist-${userId}`,
    `owned-${userId}`,
    `played-${userId}`,
    `statusIds-${userId}`
    // Adicionar outras chaves conforme necessário
  ];
  keysToRemove.forEach(key => userCollectionCache.delete(key));
};

// Funções Auxiliares de Busca

async function fetchUserGamesByUserId(userId: string): Promise<{ data: DbUserGame[] | null; error: PostgrestError | null }> {
  return supabase
    .from('user_games')
    .select('*') // Seleciona todas as colunas de user_games
    .eq('user_id', userId)
    .returns<DbUserGame[]>(); // Adicionar .returns
}

async function fetchGamesByIds(gameIds: string[]): Promise<{ data: DbGame[] | null; error: PostgrestError | null }> {
  if (gameIds.length === 0) {
    return { data: [], error: null };
  }
  return supabase
    .from('games')
    .select('*') // Seleciona todas as colunas de games
    .in('id', gameIds)
    .returns<DbGame[]>(); // Adicionar .returns
}

// Função Auxiliar de Junção

function joinUserGamesWithGames(userGames: DbUserGame[], games: DbGame[]): UserGame[] {
  const gamesMap: { [key: string]: Game } = games.reduce((acc: { [key: string]: Game }, dbGame) => {
    if (dbGame && typeof dbGame === 'object' && dbGame.id) {
      acc[dbGame.id] = {
        ...dbGame, 
        // Garante que stats seja GameStats | undefined, conforme AppGame
        stats: dbGame.stats ?? undefined 
      };
    }
    return acc;
  }, {});

  return userGames.map(userGame => ({
    ...userGame,
    play_count: userGame.play_count ?? 0,
    game: userGame.game_id ? gamesMap[userGame.game_id] || undefined : undefined
  })).filter((item): item is UserGame & { game: Game } => 
    // Garante que apenas itens com um objeto 'game' válido sejam retornados
    !!item.game && typeof item.game === 'object' && item.game.id !== undefined
  );
}

// Interfaces e Tipos

export interface CollectionFilters {
  type: 'all' | 'owned' | 'wishlist' | 'played';
  searchText?: string | null;
}

export interface CollectionSort {
  sortBy: 'game_name' | 'ug_rating' | 'ug_updated_at' | 'g_average_rating' | 'g_complexity' | 'g_year' | 'ug_play_count';
  ascending: boolean;
}

export interface Pagination {
  page: number; // 1-based index
  pageSize: number;
}

export interface PagedCollectionResult {
  data: UserGame[];
  count: number | null;
}

const getPaginationRange = (page: number, pageSize: number) => {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  return { from, to };
};

// Funções Principais (Refatoração Pendente)

// Tipo para o resultado da RPC (espelha a definição RETURNS TABLE)
// O campo 'game' é JSONB na RPC, que supabase-js deve desserializar para um objeto
// O campo 'total_records' contém a contagem total.
type RpcUserGameResult = Omit<UserGame, 'game'> & { 
    game: Game; // Esperamos que o JSONB seja desserializado para Game
    total_records: number;
};

export async function getPagedUserCollection(
  userId: string,
  filters: CollectionFilters,
  sort: CollectionSort,
  pagination: Pagination
): Promise<PagedCollectionResult> {
  console.log(`[getPagedUserCollection RPC] Buscando para User: ${userId}, Page: ${pagination.page}, Size: ${pagination.pageSize}, Filters: ${JSON.stringify(filters)}, Sort: ${JSON.stringify(sort)}`);

  // Mapear sortBy do frontend para o valor esperado pela RPC, se necessário.
  // Neste caso, os valores já coincidem ('game_name', 'ug_rating', 'ug_updated_at').
  const rpcSortBy = sort.sortBy;

  const params = {
    p_user_id: userId,
    p_filter_type: filters.type,
    p_search_text: filters.searchText || '',
    p_sort_by: rpcSortBy,
    p_sort_asc: sort.ascending,
    p_limit: pagination.pageSize,
    p_offset: (pagination.page - 1) * pagination.pageSize
  };

  try {
    const { data, error } = await supabase.rpc('get_user_collection_paged', params)
                                         .returns<RpcUserGameResult[]>(); // Especifica o tipo de retorno esperado

    if (error) {
      console.error('[getPagedUserCollection RPC] Erro ao chamar RPC:', error);
      throw error;
    }
    
    if (!data) {
      console.log('[getPagedUserCollection RPC] RPC não retornou dados.');
          return { data: [], count: 0 };
        }
        
    console.log(`[getPagedUserCollection RPC] RPC retornou ${data.length} registros.`);

    // Extrair a contagem total do primeiro registro (ou 0 se não houver dados)
    const count = data.length > 0 ? data[0].total_records : 0;

    // Mapear o resultado da RPC para UserGame[] (removendo total_records)
    // A desserialização do JSONB 'game' é feita pelo supabase-js
    // A RPC já trata play_count nulo
    const userGames: UserGame[] = data.map(item => {
        const { total_records, ...userGameData } = item; 
        // Validar se 'game' foi desserializado corretamente (opcional, mas bom)
        if (!userGameData.game || typeof userGameData.game !== 'object' || !userGameData.game.id) {
            console.warn(`[getPagedUserCollection RPC] Item com game inválido:`, item);
            // Retornar um estado inválido ou filtrar depois?
            // Por enquanto, vamos manter, mas logar o aviso.
        }
        return userGameData as UserGame; // Assumindo que a estrutura bate
    });
    
    // Filtrar itens que podem ter tido problema na desserialização do 'game'
    const validUserGames = userGames.filter(ug => ug.game && ug.game.id);
    if(validUserGames.length !== userGames.length) {
         console.warn(`[getPagedUserCollection RPC] Alguns itens foram filtrados devido a game inválido após desserialização.`);
    }

    return { data: validUserGames, count };

  } catch (error) {
    console.error(`[getPagedUserCollection RPC] Exceção ao buscar coleção para ${userId}:`, error);
    // Retornar estado de erro consistente
    return { data: [], count: 0 };
  }
}

// Refatoração das funções antigas pendente (getUserCollection, getWishlistGames, etc.)
// ... (manter funções antigas por enquanto para não quebrar a aplicação)

// Nova função para buscar toda a coleção sem paginação e com todos os dados para estatísticas
export async function getFullUserCollection(userId: string): Promise<UserGame[]> {
  if (!userId) return [];
  console.log(`[getFullUserCollection] Buscando coleção completa para estatísticas do usuário ${userId}`);
  try {
    // Tentativa de usar o nome da constraint da FK explicitamente
    // Se user_games_game_id_fkey não for o nome correto, esta query falhará.
    const { data, error } = await supabase
      .from('user_games')
      .select(`
        *,
        game:games!user_games_game_id_fkey!inner(
          id, name, year, description, image_url, thumb_url, categories, mechanics, themes, 
          min_players, max_players, play_time, complexity, average_rating, created_at, updated_at, slug,
          min_age, ano_nacional, bgg_id, ludopedia_id, artists, designers, stats
        )
      `)
      .eq('user_id', userId);

    if (error) {
      console.error(`[getFullUserCollection] Erro ao buscar coleção completa para o usuário ${userId}:`, error);
      // Logar os detalhes do erro para ajudar a depurar o nome da FK
      if (error.details && Array.isArray(error.details)) {
        console.error('[getFullUserCollection] Detalhes do erro PGRST201:', JSON.stringify(error.details, null, 2));
      }
      throw error;
    }

    // Ajustar 'game' para o tipo esperado e garantir play_count
    return (data || []).map(ug => ({
      ...ug,
      play_count: ug.play_count ?? 0,
      game: ug.game as Game 
    }));
  } catch (error) {
    console.error(`[getFullUserCollection] Exceção ao buscar coleção completa para ${userId}:`, error);
    return [];
  }
}

export async function getUserCollection(userId: string) {
    console.warn("[getUserCollection] Esta função está obsoleta, use getPagedUserCollection ou getFullUserCollection para dados completos.");
    // Chama a nova função que busca dados frescos sem cache interno.
    return await getFullUserCollection(userId);
}

export async function getUserCollectionStatusIds(userId: string): Promise<{
  ownedIds: Set<string>;
  wishlistIds: Set<string>;
    playedIds: Set<string>; // Considerar se 'played' vem de user_games ou game_sessions
}> {
    const cacheKey = `statusIds-${userId}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const ownedIds = new Set<string>();
    const wishlistIds = new Set<string>();
    const playedIds = new Set<string>(); // Precisa definir a origem (user_games.play_count > 0 ?)

    try {
    const { data, error } = await supabase
      .from('user_games')
      .select('game_id, owned, wishlist, play_count')
            .eq('user_id', userId);

    if (error) {
            console.error('Erro ao buscar status IDs:', error);
      throw error;
    }

    data?.forEach(item => {
            if (!item.game_id) return;
      if (item.owned) {
        ownedIds.add(item.game_id);
      }
      if (item.wishlist) {
        wishlistIds.add(item.game_id);
      }
            if (item.play_count && item.play_count > 0) { // Exemplo de lógica para 'played'
        playedIds.add(item.game_id);
      }
    });

        const result = { ownedIds, wishlistIds, playedIds };
        setCachedData(cacheKey, result);
        return result;
  } catch (error) {
        console.error(`Erro ao buscar IDs de status para usuário ${userId}:`, error);
        return { ownedIds, wishlistIds, playedIds }; // Retorna sets vazios em caso de erro
  }
}


export async function getWishlistGames(userId: string) {
    console.warn("[getWishlistGames] Esta função está obsoleta, use getPagedUserCollection.")
    const cacheKey = `wishlist-${userId}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
    const { data, error } = await supabase
      .from('user_games')
            .select('*, game:games(*)')
      .eq('user_id', userId)
      .eq('wishlist', true);

    if (error) {
            console.error('Erro ao buscar wishlist:', error);
      throw error;
    }
    
        // @ts-ignore
        const validData = (data || []).filter(item => !!item.game);
        setCachedData(cacheKey, validData);
        return validData;

  } catch (error) {
    console.error(`Erro ao buscar jogos da wishlist do usuário ${userId}:`, error);
        return [];
    }
}

export async function getOwnedGames(userId: string) {
    console.warn("[getOwnedGames] Esta função está obsoleta, use getPagedUserCollection.")
    const cacheKey = `owned-${userId}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    try {
    const { data, error } = await supabase
      .from('user_games')
            .select('*, game:games(*)')
      .eq('user_id', userId)
      .eq('owned', true);

    if (error) {
            console.error('Erro ao buscar coleção owned:', error);
      throw error;
    }
    
        // @ts-ignore
        const validData = (data || []).filter(item => !!item.game);
        setCachedData(cacheKey, validData);
        return validData;

  } catch (error) {
    console.error(`Erro ao buscar jogos na coleção do usuário ${userId}:`, error);
        return [];
    }
}

export async function getPlayedGames(userId: string) {
    console.warn("[getPlayedGames] Esta função está obsoleta, use getPagedUserCollection com filtro apropriado ou lógica de game_sessions.")
    // A lógica atual baseada em user_games pode não ser a ideal para "jogados"
    // Poderia ser baseado em play_count > 0 ou buscando de game_sessions
    const cacheKey = `played-${userId}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('user_games')
            .select('*, game:games(*)')
      .eq('user_id', userId)
      .gt('play_count', 0);

    if (error) throw error;
    
        // @ts-ignore
        const validData = (data || []).filter(item => !!item.game);
        setCachedData(cacheKey, validData);
        return validData;

  } catch (error) {
    console.error(`Erro ao buscar jogos jogados pelo usuário ${userId}:`, error);
    throw error;
  }
}

// Funções de Mutação (Refatoração Parcial)

// Função auxiliar para buscar o estado atual de um user_game
async function getCurrentUserGame(userId: string, gameId: string): Promise<DbUserGame | null> {
      const { data, error } = await supabase
        .from('user_games')
    .select('*')
    .eq('user_id', userId)
    .eq('game_id', gameId)
    .maybeSingle<DbUserGame>(); // Usar maybeSingle para retornar null se não existir
      
      if (error) {
    console.error(`Erro ao buscar user_game atual para ${userId}/${gameId}:`, error);
    // Dependendo da criticidade, pode-se lançar o erro ou retornar null
    return null;
  }
  return data;
}

async function upsertUserGame(userId: string, gameId: string, values: Partial<DbUserGame>): Promise<UserGame | null> {
  console.log(`[upsertUserGame] Upserting para User: ${userId}, Game: ${gameId}, Valores:`, values);
  const { data, error } = await supabase
      .from('user_games')
    .upsert({ user_id: userId, game_id: gameId, ...values }, { onConflict: 'user_id, game_id' })
      .select('*')
    .single<DbUserGame>(); // Usar single aqui, pois esperamos que sempre exista após upsert

  if (error) {
    console.error(`Erro no upsert de user_game para ${userId}/${gameId}:`, error);
    // Retornar null em caso de erro para tratamento na função chamadora
    return null;
  }
  console.log(`[upsertUserGame] Upsert bem-sucedido, resultado:`, data);
  // Mapear play_count null para 0 para satisfazer UserGame
  return data ? { ...data, play_count: data.play_count ?? 0 } : null;
}

export async function addToCollection(userId: string, gameId: string, isOwned: boolean = false, isWishlist: boolean = false) {
    console.log(`[addToCollection] User: ${userId}, Game: ${gameId}, Owned: ${isOwned}, Wishlist: ${isWishlist}`);
    // Garante play_count=0 se for uma nova entrada via upsert
    const result = await upsertUserGame(userId, gameId, { owned: isOwned, wishlist: isWishlist, play_count: 0 });

    // Chamar gamificação APÓS sucesso
    if (result) {
        if (isOwned && XP_ACTIONS.ADD_GAME_TO_COLLECTION) {
            processAction({
                supabase: supabase,
                userId: userId,
                actionKey: XP_ACTIONS.ADD_GAME_TO_COLLECTION,
                sourceId: result.id, 
                details: { gameId: gameId }
            }).catch((err: any) => console.warn(`[Gamification Warning] Failed processAction for ADD_GAME_TO_COLLECTION game ${gameId}:`, err));
        }
        if (isWishlist && XP_ACTIONS.ADD_GAME_TO_WISHLIST) {
             processAction({
                supabase: supabase,
                userId: userId,
                actionKey: XP_ACTIONS.ADD_GAME_TO_WISHLIST,
                sourceId: result.id, 
                details: { gameId: gameId }
            }).catch((err: any) => console.warn(`[Gamification Warning] Failed processAction for ADD_GAME_TO_WISHLIST game ${gameId}:`, err));
        }
    }
    return result;
}

export async function toggleOwned(userId: string, gameId: string, isOwned: boolean): Promise<UserGame | null> {
  console.log(`[toggleOwned] User: ${userId}, Game: ${gameId}, Definindo Owned para: ${isOwned}`);
  invalidateUserCache(userId);

  // 1. Buscar o estado ATUAL antes de qualquer modificação
  const currentUserGame = await getCurrentUserGame(userId, gameId);
  const wasPreviouslyOwned = currentUserGame?.owned ?? false; // Assume false se não existir ou owned for null

  // 2. Determinar se o XP deve ser concedido (apenas se mudando de !owned para owned)
  const shouldGrantXp = isOwned && !wasPreviouslyOwned;

  // 3. Realizar o Upsert (atualiza ou insere)
  // Garantir que play_count não seja sobrescrito se já existir e não for fornecido em 'values'
  // No entanto, o upsert padrão com onConflict fará a coisa certa: atualizará owned/wishlist
  // e manterá outros campos como play_count se não estiverem no objeto 'values'.
  const updatedUserGameDb = await upsertUserGame(userId, gameId, { owned: isOwned });

  if (!updatedUserGameDb) {
     console.error(`[toggleOwned] Falha ao atualizar/inserir user_game para ${userId}/${gameId}`);
     return null; // Falha na operação do banco
  }

  // 4. Conceder XP SE aplicável
  if (shouldGrantXp) {
    try {
      // Usar a actionKey diretamente
      console.log(`[toggleOwned] Concedendo XP para ${XP_ACTIONS.ADD_GAME_TO_COLLECTION}`);
      // Passar um único objeto como argumento, incluindo supabase e sourceId
      await processAction({ 
        supabase: supabase, 
        userId: userId, 
        actionKey: XP_ACTIONS.ADD_GAME_TO_COLLECTION, 
        sourceId: gameId, // Adicionar gameId como sourceId
        details: { gameId: gameId } // Manter details por enquanto
      });
      // Lidar com o resultado de processAction se necessário (ex: exibir mensagem)
    } catch (xpError) {
      // Usar a actionKey diretamente
      console.error(`[toggleOwned] Erro ao conceder XP ${XP_ACTIONS.ADD_GAME_TO_COLLECTION}:`, xpError);
      // Continuar mesmo se o XP falhar? Ou retornar um erro parcial?
      // Por enquanto, logamos o erro mas retornamos o estado do jogo atualizado.
    }
  } else {
      console.log(`[toggleOwned] XP não concedido (isOwned: ${isOwned}, wasPreviouslyOwned: ${wasPreviouslyOwned})`);
  }

  // 5. Verificar se o registro deve ser removido (se owned e wishlist se tornaram false)
  // Usar o estado atualizado do banco (updatedUserGameDb)
  if (!isOwned && !(updatedUserGameDb.wishlist ?? false)) {
    console.log(`[toggleOwned] Owned e Wishlist são false. Removendo registro para ${userId}/${gameId}`);
    await removeFromCollection(userId, gameId);
    return null; // O registro foi removido
  }

  // 6. Enriquecer com dados do jogo e retornar o tipo correto UserGame
  // Se chegou aqui, o registro ainda existe. Precisamos buscar o jogo associado.
  // Assumindo que a função getPagedUserCollection ou uma similar possa buscar um único item
  // ou podemos fazer uma busca simples pelo jogo.
   const { data: gameData, error: gameError } = await supabase
        .from('games')
        .select('*')
        .eq('id', gameId)
        .maybeSingle<DbGame>(); // Usar DbGame aqui

    if (gameError || !gameData) {
        console.error(`[toggleOwned] Erro ao buscar detalhes do jogo ${gameId}:`, gameError);
        // Retornar o UserGame sem os detalhes do jogo? Ou null?
        // Retornar com game undefined pode ser aceitável dependendo da UI.
         return { ...updatedUserGameDb, play_count: updatedUserGameDb.play_count ?? 0, game: undefined };
    }

    // Ajuste para garantir que stats seja GameStats | undefined
    const game: Game = { ...gameData, stats: gameData.stats ?? undefined };
    const result: UserGame = { ...updatedUserGameDb, play_count: updatedUserGameDb.play_count ?? 0, game };

    return result;
}

export async function toggleWishlist(userId: string, gameId: string, isWishlist: boolean): Promise<UserGame | null> {
  console.log(`[toggleWishlist] User: ${userId}, Game: ${gameId}, Definindo Wishlist para: ${isWishlist}`);
  invalidateUserCache(userId);

  // 1. Buscar o estado ATUAL antes de qualquer modificação
  const currentUserGame = await getCurrentUserGame(userId, gameId);
  const wasPreviouslyWishlisted = currentUserGame?.wishlist ?? false; // Assume false se não existir ou wishlist for null

  // 2. Determinar se o XP deve ser concedido (apenas se mudando de !wishlist para wishlist)
  const shouldGrantXp = isWishlist && !wasPreviouslyWishlisted;

  // 3. Realizar o Upsert
  const updatedUserGameDb = await upsertUserGame(userId, gameId, { wishlist: isWishlist });

   if (!updatedUserGameDb) {
     console.error(`[toggleWishlist] Falha ao atualizar/inserir user_game para ${userId}/${gameId}`);
     return null; // Falha na operação do banco
  }

  // 4. Conceder XP SE aplicável
  if (shouldGrantXp) {
    try {
       // Usar a actionKey diretamente
      console.log(`[toggleWishlist] Concedendo XP para ${XP_ACTIONS.ADD_GAME_TO_WISHLIST}`);
       // Passar um único objeto como argumento, incluindo supabase e sourceId
      await processAction({ 
        supabase: supabase, 
        userId: userId, 
        actionKey: XP_ACTIONS.ADD_GAME_TO_WISHLIST, 
        sourceId: gameId, // Adicionar gameId como sourceId
        details: { gameId: gameId } // Manter details por enquanto
      });
    } catch (xpError) {
       // Usar a actionKey diretamente
      console.error(`[toggleWishlist] Erro ao conceder XP ${XP_ACTIONS.ADD_GAME_TO_WISHLIST}:`, xpError);
    }
  } else {
      console.log(`[toggleWishlist] XP não concedido (isWishlist: ${isWishlist}, wasPreviouslyWishlisted: ${wasPreviouslyWishlisted})`);
  }

  // 5. Verificar se o registro deve ser removido (se owned e wishlist se tornaram false)
  if (!isWishlist && !(updatedUserGameDb.owned ?? false)) {
    console.log(`[toggleWishlist] Owned e Wishlist são false. Removendo registro para ${userId}/${gameId}`);
    await removeFromCollection(userId, gameId);
    return null; // O registro foi removido
  }

  // 6. Enriquecer com dados do jogo e retornar o tipo correto UserGame
   const { data: gameData, error: gameError } = await supabase
        .from('games')
      .select('*')
        .eq('id', gameId)
        .maybeSingle<DbGame>();

    if (gameError || !gameData) {
        console.error(`[toggleWishlist] Erro ao buscar detalhes do jogo ${gameId}:`, gameError);
         return { ...updatedUserGameDb, play_count: updatedUserGameDb.play_count ?? 0, game: undefined };
    }

    const game: Game = { ...gameData, stats: gameData.stats ?? undefined };
    const result: UserGame = { ...updatedUserGameDb, play_count: updatedUserGameDb.play_count ?? 0, game };

  return result;
}

// togglePlayed provavelmente deveria interagir com game_sessions ou um campo específico
// A lógica atual com upsert pode não ser a ideal.
export async function togglePlayed(userId: string, gameId: string, isPlayed: boolean): Promise<UserGame | null> {
    console.warn("[togglePlayed] A lógica desta função pode precisar de revisão dependendo de como 'played' é rastreado.");
    const currentPlayCount = await getUserGamePlayCount(userId, gameId); // Retorna number (já trata null)
    
    // Se marcar como jogado e contagem é 0, define como 1.
    // Se marcar como não jogado, define como 0.
    // Senão, mantém a contagem atual (não faz nada se já > 0 e isPlayed=true)
    const newPlayCount = isPlayed && currentPlayCount === 0 ? 1 : 
                         !isPlayed ? 0 : currentPlayCount;
    
    if (newPlayCount !== currentPlayCount) {
        return updatePlayCount(userId, gameId, newPlayCount);
    }
    
    // Se não houver alteração na contagem, buscar o estado atual
    const { data: currentUserGame, error } = await supabase
      .from('user_games')
      .select('*')
      .eq('user_id', userId)
      .eq('game_id', gameId)
        .maybeSingle<DbUserGame>();
      
      if (error) {
         console.error("[togglePlayed] Erro ao buscar estado atual:", error);
      return null;
    }
    
    return currentUserGame ? { ...currentUserGame, play_count: currentUserGame.play_count ?? 0 } : null; 
}

// Retorna number (trata null)
async function getUserGamePlayCount(userId: string, gameId: string): Promise<number> {
     const { data, error } = await supabase
        .from('user_games')
        .select('play_count')
        .eq('user_id', userId)
        .eq('game_id', gameId)
        .maybeSingle<Pick<DbUserGame, 'play_count'> >();

     if(error) {
         console.error("[getUserGamePlayCount] Erro:", error);
         return 0;
     }
    return data?.play_count ?? 0;
}

export async function updatePlayCount(userId: string, gameId: string, newPlayCount?: number): Promise<UserGame | null> {
    console.log(`[updatePlayCount] User: ${userId}, Game: ${gameId}, New Count: ${newPlayCount}`);

    if (typeof newPlayCount === 'number' && newPlayCount >= 0) {
        // Atualizar diretamente 
        // upsertUserGame já retorna UserGame | null
        return await upsertUserGame(userId, gameId, { play_count: newPlayCount });
        // Gamificação seria aqui se necessário (comparar com valor anterior)
      } else {
        // Incrementar via RPC 
        try {
            console.log(`[updatePlayCount] Chamando RPC increment_play_count para user ${userId}, game ${gameId}`);
            // Chamar RPC e depois buscar o registro atualizado
            const { error: rpcError } = await supabase.rpc('increment_play_count', {
        p_user_id: userId,
                p_game_id: gameId
            });

            if (rpcError) {
                console.error(`[updatePlayCount] Erro na RPC increment_play_count:`, rpcError);
                throw rpcError;
            }
            
            console.log(`[updatePlayCount] RPC executado com sucesso.`);
            // Buscar o registro atualizado após a RPC para retornar
            const { data: updatedData, error: fetchError } = await supabase
        .from('user_games')
                    .select('*')
        .eq('user_id', userId)
        .eq('game_id', gameId)
                    .single<DbUserGame>(); 
            if(fetchError) throw fetchError;
      
      invalidateUserCache(userId);
            // Converter para UserGame tratando play_count
            return updatedData ? { ...updatedData, play_count: updatedData.play_count ?? 0 } : null;
      
  } catch (error) {
            console.error(`[updatePlayCount] Exceção ao incrementar play count para user ${userId}, game ${gameId}:`, error);
            return null;
        }
  }
}


export async function removeFromCollection(userId: string, gameId: string): Promise<boolean> {
    console.log(`[removeFromCollection] User: ${userId}, Game: ${gameId}`);
  try {
        const { error } = await supabase
      .from('user_games')
      .delete()
      .eq('user_id', userId)
            .eq('game_id', gameId);
    
        if (error) {
            console.error(`Erro ao remover jogo ${gameId} da coleção do usuário ${userId}:`, error);
      throw error;
    }
    
    invalidateUserCache(userId);
        return true;
  } catch (error) {
        console.error(`Exceção ao remover jogo ${gameId} da coleção do usuário ${userId}:`, error);
        return false;
    }
}

// Esta função parece não pertencer diretamente ao collectionService, pois remove histórico?
// Manter por ora, mas considerar mover para um service de game_sessions se aplicável.
export async function removePlayedGameHistory(userId: string, gameId: string): Promise<boolean> {
     console.warn("[removePlayedGameHistory] Esta função apenas zera o play_count em user_games, não remove game_sessions.");
    try {
        // Zerar play_count
        const { error } = await supabase
      .from('user_games')
            .update({ play_count: 0 })
      .eq('user_id', userId)
      .eq('game_id', gameId);

        if (error) {
            console.error(`Erro ao zerar play count para ${gameId} do usuário ${userId}:`, error);
            throw error;
        }

    invalidateUserCache(userId);
    return true;
  } catch (error) {
        console.error(`Exceção ao remover histórico jogado (zerar play_count) para jogo ${gameId} do usuário ${userId}:`, error);
    return false;
  }
} 